export const getStoredUsername = () => localStorage.getItem('username') || '';
export const setStoredUsername = u => (u ? localStorage.setItem('username', u) : localStorage.removeItem('username'));
export const setStoredUserId = id =>
    id ? localStorage.setItem('userId', String(id)) : localStorage.removeItem('userId');

const API_BASE = '/api/v1';
const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [1000, 3000];

export class ApiError extends Error {
    constructor(status, message) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

export function isAuthError(err) {
    return err instanceof ApiError && err.status === 401;
}

const FRIENDLY_MESSAGES = {
    400: 'Invalid request. Please check your input.',
    403: 'You don\u2019t have permission to do that.',
    404: 'The item you\u2019re looking for no longer exists.',
    409: 'This conflicts with an existing item.',
    500: 'Something went wrong on the server. Please try again.',
    502: 'Server is temporarily unavailable. Please try again.',
    503: 'Server is temporarily unavailable. Please try again.',
};

async function parseServerHint(res) {
    try {
        const text = await res.text();
        if (!text) return null;
        if (text.includes('ECONNREFUSED') || text.includes('Proxy error'))
            return 'Server is unreachable. Please try again later.';
        try {
            const data = JSON.parse(text);
            if (data.error) return data.error;
        } catch {}
        return null;
    } catch {
        return null;
    }
}

function friendlyMessage(status, serverHint) {
    if (serverHint) return serverHint;
    return FRIENDLY_MESSAGES[status] || 'Something went wrong. Please try again.';
}

function isRetryable(method, err) {
    // Network errors (request never reached server) — safe to retry any method
    if (err instanceof ApiError && err.status === 0) return true;
    // 5xx errors — only retry idempotent GET/HEAD requests
    if (err instanceof ApiError && err.status >= 500) {
        const m = (method || 'GET').toUpperCase();
        return m === 'GET' || m === 'HEAD';
    }
    return false;
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export async function apiFetch(url, options = {}) {
    const {timeout = DEFAULT_TIMEOUT_MS, signal: callerSignal, ...restOptions} = options;

    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
            await sleep(RETRY_DELAYS_MS[attempt - 1]);
        }

        // If the caller already aborted, bail out before retrying
        if (callerSignal?.aborted) {
            throw new DOMException('The operation was aborted.', 'AbortError');
        }

        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

        // Link caller signal to our controller so either can abort
        const onCallerAbort = () => timeoutController.abort();
        callerSignal?.addEventListener('abort', onCallerAbort, {once: true});

        let res;
        try {
            res = await fetch(API_BASE + url, {
                ...restOptions,
                credentials: 'same-origin',
                signal: timeoutController.signal,
            });
        } catch (err) {
            clearTimeout(timeoutId);
            callerSignal?.removeEventListener('abort', onCallerAbort);
            if (callerSignal?.aborted) {
                throw new DOMException('The operation was aborted.', 'AbortError');
            }
            if (err.name === 'AbortError') {
                throw new ApiError(0, 'Request timed out. Please try again.');
            }
            lastError = new ApiError(0, 'Network error. Check your connection and try again.');
            if (attempt < MAX_RETRIES && isRetryable(restOptions.method, lastError)) continue;
            throw lastError;
        } finally {
            clearTimeout(timeoutId);
            callerSignal?.removeEventListener('abort', onCallerAbort);
        }

        if (res.status === 401) {
            window.dispatchEvent(new Event('auth:logout'));
            throw new ApiError(401, 'Session expired');
        }
        if (!res.ok) {
            const serverHint = await parseServerHint(res);
            lastError = new ApiError(res.status, friendlyMessage(res.status, serverHint));
            if (attempt < MAX_RETRIES && isRetryable(restOptions.method, lastError)) continue;
            throw lastError;
        }
        return res;
    }
    throw lastError;
}

export async function fetchProfile() {
    const res = await apiFetch('/users/me');
    return res.json();
}

export async function updateProfile(displayName) {
    const res = await apiFetch('/users/me', {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({displayName}),
    });
    return res.json();
}

export async function changePassword(currentPassword, newPassword) {
    const res = await apiFetch('/users/me/change-password', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({currentPassword, newPassword}),
    });
    return res.json();
}

export async function apiFetchPublic(url, options = {}) {
    const {timeout = DEFAULT_TIMEOUT_MS, signal: callerSignal, includeAuth = false, ...restOptions} = options;

    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
            await sleep(RETRY_DELAYS_MS[attempt - 1]);
        }

        if (callerSignal?.aborted) {
            throw new DOMException('The operation was aborted.', 'AbortError');
        }

        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

        const onCallerAbort = () => timeoutController.abort();
        callerSignal?.addEventListener('abort', onCallerAbort, {once: true});

        let res;
        try {
            res = await fetch(API_BASE + url, {
                ...restOptions,
                // When includeAuth is set, forward the session cookie so the
                // backend can recognise the share's own creator and skip
                // logging preview hits as recipient activity. Anonymous
                // recipients leave includeAuth false and stay cookie-less.
                credentials: includeAuth ? 'same-origin' : 'omit',
                signal: timeoutController.signal,
            });
        } catch (err) {
            clearTimeout(timeoutId);
            callerSignal?.removeEventListener('abort', onCallerAbort);
            if (callerSignal?.aborted) {
                throw new DOMException('The operation was aborted.', 'AbortError');
            }
            if (err.name === 'AbortError') {
                throw new ApiError(0, 'Request timed out. Please try again.');
            }
            lastError = new ApiError(0, 'Network error. Check your connection and try again.');
            if (attempt < MAX_RETRIES && isRetryable(restOptions.method, lastError)) continue;
            throw lastError;
        } finally {
            clearTimeout(timeoutId);
            callerSignal?.removeEventListener('abort', onCallerAbort);
        }

        if (!res.ok) {
            const serverHint = await parseServerHint(res);
            lastError = new ApiError(res.status, friendlyMessage(res.status, serverHint));
            if (attempt < MAX_RETRIES && isRetryable(restOptions.method, lastError)) continue;
            throw lastError;
        }
        return res;
    }
    throw lastError;
}
